import http, { API_BASE } from './http';

export default {
  // `userId` is kept for signature compatibility; the backend scopes the list by
  // the JWT identity and ignores any client-supplied id.
  async list(userId, opts = {}) {
    const params = new URLSearchParams();
    if (opts.status)       params.set('status', opts.status);
    if (opts.spawned_from) params.set('spawned_from', String(opts.spawned_from));
    if (opts.limit)        params.set('limit', String(opts.limit));

    const qs = params.toString();
    const url = `${API_BASE}/api/trading_bots${qs ? `?${qs}` : ''}`;

    const response = await http.get(url);
    return response.data;
  },

  // The path param is still sent for route compatibility; the backend resolves
  // the owner from the JWT.
  async getUserHistories(userId) {
    const response = await http.get(`${API_BASE}/api/userhistory/all_by_user/${userId}`);
    return response.data;
  },

  // Paginated history for a single bot (newest-first). Returns
  // { items, total, limit, offset }. Used to lazy-load a bot's history one page
  // at a time when its card is expanded, instead of downloading everything.
  async getBotHistory(botId, { limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const response = await http.get(`${API_BASE}/api/userhistory/by_bot/${botId}?${params.toString()}`);
    return response.data;
  },

  async create(botData) {
    const response = await http.post(`${API_BASE}/api/trading_bots`, botData);
    return response.data;
  },

  async update(id, updates) {
    const response = await http.put(`${API_BASE}/api/trading_bots/${id}`, updates);
    return response.data;
  },

  async delete(id) {
    await http.delete(`${API_BASE}/api/trading_bots/${id}`);
  },

  // ── Strategy versioning (backend wave 3-A, feature 6) ─────────────────────
  // Every bot strategy save writes an immutable snapshot row. These four read
  // them back and re-apply one. Paths are under /api/bots/:botId/... (NOT
  // /api/trading_bots/...) — a different router module on the backend.

  // GET /api/bots/:botId/versions -> { bot_id, total, limit, offset, versions[] }
  // `versions` is newest-first and deliberately omits trade_info (it is large);
  // fetch a single version for the full snapshot.
  async listVersions(botId, { limit = 50, offset = 0 } = {}) {
    const response = await http.get(`${API_BASE}/api/bots/${botId}/versions`, {
      params: { limit, offset },
    });
    return response.data;
  },

  // GET /api/bots/:botId/versions/:version -> { id, bot_id, version, label, created_at, snapshot }
  async getVersion(botId, version) {
    const response = await http.get(`${API_BASE}/api/bots/${botId}/versions/${version}`);
    return response.data;
  },

  // GET /api/bots/:botId/versions/:a/diff/:b
  // -> { from, to, summary, equal, truncated, added[], removed[], changed[] }
  // Paths inside added/removed/changed are dotted with [i] for array indices.
  async diffVersions(botId, a, b) {
    const response = await http.get(`${API_BASE}/api/bots/${botId}/versions/${a}/diff/${b}`);
    return response.data;
  },

  // POST /api/bots/:botId/versions/:version/redeploy — no body; the two path
  // params are the whole input. Runs through the normal bot-update handler, so
  // it can answer any of that handler's 4xx (including 409 EXCHANGE_GONE and
  // 409 PROMOTION_GATE).
  async redeployVersion(botId, version) {
    const response = await http.post(
      `${API_BASE}/api/bots/${botId}/versions/${version}/redeploy`,
      {},
    );
    return response.data;
  },

  // ── Promotion gate / report card (backend wave 3-B, feature 5) ────────────

  // GET /api/bots/:botId/report-card — recomputed on every read (paper trades
  // keep arriving; a stale `passed` is the failure mode that costs money).
  async getReportCard(botId) {
    const response = await http.get(`${API_BASE}/api/bots/${botId}/report-card`);
    return response.data;
  },

  // POST /api/bots/:botId/report-card/waive { reason } — reason is 10..2000
  // chars, recorded verbatim. A waiver is a decision, not a measurement, so it
  // is never recomputed away.
  async waiveReportCard(botId, reason) {
    const response = await http.post(
      `${API_BASE}/api/bots/${botId}/report-card/waive`,
      { reason },
    );
    return response.data;
  },
};
